"use client";

import { CampaignIcon } from "@/features/campaigns/catalog";
import { useI18n } from "@/i18n/I18nProvider";
import { formatLocalizedAmount, localizedText } from "@/i18n/text";
import { cn } from "@/lib/utils";
import type { ListingCampaignDetailsDTO } from "@/types/campaign";

type Props = {
  campaigns?: ListingCampaignDetailsDTO | null;
  className?: string;
};

/**
 * Every offer on a listing, on the detail page.
 *
 * Styled as one more section of the page — the same white surface, shadow and
 * corner radius as the sections around it — rather than as a tinted callout.
 * An offer is information about the tenancy, like the dates and the floor; a
 * green panel with icon tiles shouted for attention the content had already
 * earned with a struck-through price, and read as pasted-in rather than
 * built-in.
 *
 * The small print is the reason the section exists at all: a discount with
 * conditions is only honest if the conditions sit where the student reads them
 * before applying. Every string is written by the backend or the company — the
 * client decides layout and nothing else.
 */
export default function CampaignBlock({ campaigns, className }: Props) {
  const { locale } = useI18n();
  const items = campaigns?.items ?? [];

  if (items.length === 0) {
    return null;
  }

  return (
    <section
      className={cn(
        "rounded-2xl border border-black/5 bg-white/80 p-4 shadow-[0_18px_45px_rgba(0,0,0,0.05)] sm:rounded-3xl sm:p-6",
        className
      )}
      aria-label={localizedText(locale, "Erbjudanden", "Offers")}
    >
      <h2 className="text-xs font-semibold uppercase tracking-[0.1em] text-gray-500">
        {localizedText(locale, "Erbjudanden", "Offers")}
      </h2>

      <ul className="mt-3 divide-y divide-gray-100">
        {items.map((item) => (
          <li key={item.id} className="py-4 first:pt-1 last:pb-0">
            <div className="flex items-start gap-2.5">
              <CampaignIcon
                iconKey={item.iconKey}
                className={cn(
                  "mt-[3px] h-4 w-4 shrink-0",
                  item.effect === "NEUTRAL" ? "text-gray-400" : "text-brand"
                )}
              />

              <div className="flex min-w-0 flex-1 flex-col gap-1">
                <p className="text-[15px] font-semibold leading-snug text-gray-900">
                  {item.headline || item.title}
                </p>

                {item.description && (
                  <p className="text-sm leading-relaxed text-gray-600">
                    {item.description}
                  </p>
                )}

                {item.schedule && item.schedule.length > 0 && (
                  /* The whole ladder, because the question a student is really
                     asking is what year two costs — and a card has no room to
                     answer it. The row in force today is bold, so the price at
                     the top of the page is traceable to a line here. */
                  <table className="mt-2 w-full max-w-md border-separate border-spacing-0 text-sm">
                    <caption className="sr-only">
                      {localizedText(locale, "Hyra per period", "Rent by period")}
                    </caption>
                    <tbody>
                      {item.schedule.map((row) => (
                        <tr
                          key={`${row.periodLabel}-${row.discountLabel}`}
                          className={
                            row.current
                              ? "font-semibold text-gray-900"
                              : "text-gray-500"
                          }
                        >
                          <th
                            scope="row"
                            className="border-t border-gray-100 py-1.5 pr-3 text-left font-[inherit]"
                          >
                            {row.periodLabel}
                          </th>
                          <td className="border-t border-gray-100 py-1.5 pr-3">
                            {row.discountLabel}
                          </td>
                          <td className="border-t border-gray-100 py-1.5 text-right tabular-nums">
                            {typeof row.rent === "number"
                              ? `${formatLocalizedAmount(locale, row.rent)}${localizedText(locale, "/mån", "/mo")}`
                              : "—"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}

                {(item.validityLabel || item.terms) && (
                  <p className="text-xs leading-relaxed text-gray-500">
                    {[item.validityLabel, item.terms].filter(Boolean).join(" · ")}
                  </p>
                )}
              </div>
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}
